import getImagePath from "@Utils/getImagePath";
import {
  getVideoSrc,
  getVideoThumb,
} from "@Pages/PropertyPage/components/SectionFeatured.tsx/utils";
import {
  applyReviewParamOrder,
  LEGACY_MEDIA_PARAMS,
  LEGACY_REVIEW_PARAMS,
  REVIEW_ALL_VALUE,
  REVIEW_PARAMS,
} from "@Pages/PropertyPage/data/reviewDeepLink";
import { PhotoItem, ReviewMediaItem, ReviewPhoto } from "./types";

/**
 * Video host used by review media. The reviews API does not return a platform
 * per item, so this is the single place that assumption lives - both
 * buildReviewsData and the thumbnail helpers read it from here.
 */
export const REVIEW_VIDEO_PLATFORM = "gumlet";

export const REVIEW_MEDIA_SIZES = {
  card: { width: 64, height: 64 },
  overview: { width: 140, height: 140 },
  overviewCompact: { width: 100, height: 80 },
  galleryRailMobile: { width: 56, height: 56 },
  galleryRailDesktop: { width: 200, height: 112 },
} as const;

type ReviewMediaSize = keyof typeof REVIEW_MEDIA_SIZES;

/** Any of the review media shapes that can be rendered as a thumbnail. */
type ReviewMediaLike = Partial<PhotoItem> & Partial<ReviewPhoto> & ReviewMediaItem;

const isVideoMedia = (media: ReviewMediaLike): boolean =>
  media.isVideo ?? media.category === "videos";

export function getReviewMediaThumbUrl(media: ReviewMediaLike, size: ReviewMediaSize): string {
  const { width, height } = REVIEW_MEDIA_SIZES[size];
  const isVideo = isVideoMedia(media);

  // For videos the poster image lives on `thumbnail_url`; `path` is the
  // playable video URL and must never be used as an image source. Note the
  // fallbacks use `||` rather than `??` so an empty string falls through.
  const thumbPath = isVideo
    ? media.thumbnail_url || media.src || media.base_path || ""
    : media.src || media.base_path || media.path || media.thumbnail_url || "";

  if (!thumbPath) return "";

  if (isVideo) {
    try {
      return (
        getVideoThumb(
          {
            thumbnail_url: thumbPath,
            path: media.path || thumbPath,
            platform: media.platform || REVIEW_VIDEO_PLATFORM,
          },
          { width, height },
        ) || thumbPath
      );
    } catch {
      // getGumletThumbnail does `new URL(...)` and getYoutubeVideoThumbUrl
      // splits on "v=" - both throw on a relative or malformed URL. Review
      // media is user supplied, so degrade to the raw thumbnail instead of
      // failing the whole property page render (SSR 500 / error boundary).
      return thumbPath;
    }
  }

  return getImagePath(thumbPath, "thumbnail-normal", {
    w: width * 2,
    h: height * 2,
    fit: "crop",
  });
}

export function getReviewMediaFullUrl(
  media: ReviewMediaLike,
  isDesktop: boolean,
  withWatermark = true,
): string {
  const path = media.base_path || media.path || "";
  if (!path) return "";
  const modifier = isDesktop ? "gallery-full-screen-desktop" : "gallery-full-screen-mobile";
  return getImagePath(path, modifier, {}, withWatermark);
}

export { getVideoSrc, getVideoThumb };

export function calculateBarWidth(score: number, maxScore = 5): string {
  const percentage = Math.min(Math.max((score / maxScore) * 100, 0), 100);
  return `${percentage}%`;
}

function isCJKDominant(text: string): boolean {
  const cjkChars = text.match(/[\u4e00-\u9fff\u3400-\u4dbf\uf900-\ufaff]/g);
  return (cjkChars?.length || 0) > text.length * 0.3;
}

export function truncateText(text: string, maxLength = 150): string {
  const effectiveMax = isCJKDominant(text) ? Math.floor(maxLength * 0.5) : maxLength;
  if (text.length <= effectiveMax) return text;
  return `${text.slice(0, effectiveMax).trimEnd()}...`;
}

export function getInitials(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .filter((word) => /[a-zA-Z]/.test(word));

  if (words.length === 0) return "";
  const first = words[0].match(/[a-zA-Z]/)?.[0]?.toUpperCase() || "";
  if (words.length === 1) return first;
  const last = words[words.length - 1].match(/[a-zA-Z]/)?.[0]?.toUpperCase() || "";
  return first + last;
}

const NATIONALITY_TO_COUNTRY_CODE: Record<string, string> = {
  american: "US",
  british: "GB",
  english: "GB",
  scottish: "GB",
  welsh: "GB",
  irish: "IE",
  canadian: "CA",
  australian: "AU",
  german: "DE",
  french: "FR",
  spanish: "ES",
  italian: "IT",
  dutch: "NL",
  portuguese: "PT",
  brazilian: "BR",
  mexican: "MX",
  indian: "IN",
  chinese: "CN",
  china: "CN",
  japanese: "JP",
  korean: "KR",
  singaporean: "SG",
  malaysian: "MY",
  thai: "TH",
  vietnamese: "VN",
  filipino: "PH",
  indonesian: "ID",
  pakistani: "PK",
  bangladeshi: "BD",
  sri_lankan: "LK",
  nepalese: "NP",
  nigerian: "NG",
  ghanaian: "GH",
  kenyan: "KE",
  south_african: "ZA",
  egyptian: "EG",
  saudi: "SA",
  emirati: "AE",
  turkish: "TR",
  russian: "RU",
  polish: "PL",
  swedish: "SE",
  norwegian: "NO",
  danish: "DK",
  finnish: "FI",
  greek: "GR",
  swiss: "CH",
  austrian: "AT",
  belgian: "BE",
  czech: "CZ",
  hungarian: "HU",
  romanian: "RO",
  colombian: "CO",
  argentinian: "AR",
  chilean: "CL",
  peruvian: "PE",
  new_zealander: "NZ",
  "new zealander": "NZ",
};

export function getNationalityCountryCode(nationality: string): string {
  if (!nationality) return "";
  const normalized = nationality
    .split(",")[0]
    .toLowerCase()
    .trim()
    .replace(/[-_\s]+/g, "_");
  return (NATIONALITY_TO_COUNTRY_CODE[normalized] || "").toLowerCase();
}

export function getNationalityFlagEmoji(nationality: string): string {
  if (!nationality) return "";
  const normalized = nationality
    .toLowerCase()
    .trim()
    .replace(/[-_\s]+/g, "_");
  const countryCode = NATIONALITY_TO_COUNTRY_CODE[normalized];
  if (!countryCode) return "";
  const codePoints = countryCode
    .toUpperCase()
    .split("")
    .map((char) => 0x1f1e6 + char.charCodeAt(0) - 65);
  return String.fromCodePoint(...codePoints);
}

/**
 * Set (string) or clear (null) review params on the current history entry without navigating.
 * Keeps history.state so the useModal stack survives, and every unrelated param with it.
 */
export function updateReviewUrlParams(updates: Record<string, string | null>): void {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams(window.location.search);
  Object.entries(updates).forEach(([key, value]) => {
    if (value === null) params.delete(key);
    else params.set(key, value);
  });
  applyReviewParamOrder(params);
  const qs = params.toString();
  const newURL = `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash}`;
  if (newURL === `${window.location.pathname}${window.location.search}${window.location.hash}`) {
    return;
  }
  window.history.replaceState({ ...window.history.state, path: newURL }, "", newURL);
}

/**
 * The whole review-param state as one map. Every legacy spelling is cleared on each write, so
 * what the user copies is always the canonical grammar.
 *
 * `galleryOpen` is separate from `mediaNumber` being undefined: an open gallery that has not
 * reported its item yet must leave the media params alone, a closed one must clear them.
 */
export const reviewUrlParams = ({
  open,
  galleryOpen = false,
  reviewId,
  mediaNumber,
}: {
  open: boolean;
  galleryOpen?: boolean;
  reviewId?: string;
  mediaNumber?: number;
}): Record<string, string | null> => {
  const params: Record<string, string | null> = Object.fromEntries(
    [...LEGACY_MEDIA_PARAMS, ...Object.values(LEGACY_REVIEW_PARAMS)].map((key) => [key, null]),
  );
  params[REVIEW_PARAMS.ALL] = open ? REVIEW_ALL_VALUE : null;

  if (open && galleryOpen) {
    if (mediaNumber === undefined) return params; // item not reported yet - leave media alone
    params[REVIEW_PARAMS.REVIEW_ID] = reviewId ?? null;
    params[REVIEW_PARAMS.MEDIA] = String(mediaNumber);
    return params;
  }
  params[REVIEW_PARAMS.REVIEW_ID] = null;
  params[REVIEW_PARAMS.MEDIA] = null;
  return params;
};
